import { useEffect, useRef } from "react";

import styles from "./App.module.scss";
import { CameraView } from "./components/CameraView";
import { HintMessage } from "./components/HintMessage";
import { IndicatorRing } from "./components/IndicatorRing";
import { ResultMessage, type ResultVariant } from "./components/ResultMessage";
import { useVerificationFlow } from "./hooks/useVerificationFlow";
import { APIResponse } from "./lib/api.ts";
import { t } from "./i18n/translations";

function getResultVariant(serverResult?: APIResponse | null): ResultVariant {
  if (serverResult?.reason === "already_rejected") {
    return "rejected";
  }
  if (serverResult?.reason === "try_later") {
    return "warning";
  }
  return "passed";
}

function getResultMessageKey(serverResult?: APIResponse | null): string {
  if (serverResult?.reason === "already_rejected") {
    return "already_rejected_message";
  }
  if (serverResult?.reason === "try_later") {
    return "try_later_message";
  }
  return "already_passed_message";
}

export function App() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const {
    phase,
    indicatorState,
    hint,
    error,
    faceClass,
    debugResult,
    serverResult,
    startVerification,
  } = useVerificationFlow(videoRef);

  useEffect(() => {
    try {
      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.setHeaderColor("#000000");
        tg.setBackgroundColor("#000000");
        tg.setBottomBarColor("#000000");
        tg.expand();
      }
    } catch (e) {
      console.warn("Telegram WebApp not available:", e);
    }
  }, []);

  useEffect(() => {
    if (phase === "idle") {
      startVerification();
    }
  }, [phase, startVerification]);

  const isBlurred = phase === "loading";
  const showResult = !!serverResult && !serverResult.ok;
  const resultVariant = getResultVariant(serverResult);
  const resultMessageKey = getResultMessageKey(serverResult);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center safe-area-padding">
      {!showResult && (
        <div
          className={`${styles.cameraRingContainer} relative rounded-full overflow-visible`}
        >
          <div className="absolute inset-[12.5%] rounded-full overflow-hidden">
            <CameraView ref={videoRef} isBlurred={isBlurred} />
          </div>
          <IndicatorRing state={indicatorState} faceClass={faceClass} />
        </div>
      )}

      {showResult && (
        <ResultMessage variant={resultVariant} messageKey={resultMessageKey} />
      )}

      {error && !showResult && (
        <div className="mt-6 text-center text-red-500 text-sm max-w-[90vw]">
          {t(error)}
        </div>
      )}

      {!showResult && <HintMessage hint={hint} />}

      {debugResult &&
        typeof window !== "undefined" &&
        window.location.href.endsWith("?debug") && (
          <div className="mt-6 text-center font-sans text-sm">
            <div id="age">Age: {debugResult.bucket}</div>
            <div
              id="result"
              className={debugResult.passed ? "text-green-300" : "text-red-500"}
            >
              {debugResult.passed ? "Passed." : "Not Passed."}
            </div>
          </div>
        )}
    </div>
  );
}
